import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Grid, TextField } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import {
  useKoreanLanguage2,
  useKoreanSchoolCredentials,
  type SchoolItem,
  type UpdateSchoolPayload,
} from '../Hooks/useKoreanLanguageForm'
import { toast } from 'react-toastify'

type Props = {
  open: boolean
  onClose: () => void
  school: SchoolItem
  regionCode: number
}

type SchoolForm = {
  schoolNameUz: string
  schoolNameRu: string
  schoolNameKo: string
  login: string
  password: string
}

const emptyForm: SchoolForm = {
  schoolNameUz: '',
  schoolNameRu: '',
  schoolNameKo: '',
  login: '',
  password: '',
}

const getFormFromSchool = (school: SchoolItem): SchoolForm => ({
  schoolNameUz: school.schoolNameUz ?? '',
  schoolNameRu: school.schoolNameRu ?? '',
  schoolNameKo: school.schoolNameKo ?? '',
  login: school.login ?? '',
  password: school.password ?? '',
})

export const KoreanLanguageSchoolEditDialog = ({ open, onClose, school, regionCode }: Props) => {
  const { updateSchoolMutation } = useKoreanLanguage2()
  const { data: credentials, isFetching: isCredentialsFetching } = useKoreanSchoolCredentials(open ? school.schoolId : null)

  const [form, setForm] = useState<SchoolForm>(emptyForm)
  const [initialForm, setInitialForm] = useState<SchoolForm>(emptyForm)

  const [touched, setTouched] = useState({
    schoolNameUz: false,
    schoolNameRu: false,
    schoolNameKo: false,
  })

  useEffect(() => {
    if (!school) return

    const nextForm = getFormFromSchool(school)
    setForm(nextForm)
    setInitialForm(nextForm)
    setTouched({
      schoolNameUz: false,
      schoolNameRu: false,
      schoolNameKo: false,
    })
  }, [school])

  useEffect(() => {
    if (!credentials) return

    const nextForm = {
      schoolNameUz: credentials.schoolNameUz ?? school.schoolNameUz ?? '',
      schoolNameRu: credentials.schoolNameRu ?? school.schoolNameRu ?? '',
      schoolNameKo: credentials.schoolNameKo ?? school.schoolNameKo ?? '',
      login: credentials.login ?? '',
      password: credentials.password ?? '',
    }

    setForm(nextForm)
    setInitialForm(nextForm)
  }, [credentials, school])

  const rawErrors = useMemo(
    () => ({
      schoolNameUz: !form.schoolNameUz.trim(),
      schoolNameRu: !form.schoolNameRu.trim(),
      schoolNameKo: !form.schoolNameKo.trim(),
    }),
    [form],
  )

  const hasError = rawErrors.schoolNameUz || rawErrors.schoolNameRu || rawErrors.schoolNameKo

  const handleChange =
    (field: keyof SchoolForm) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      setForm(prev => ({ ...prev, [field]: value }))
    }

  const handleBlur = (field: 'schoolNameUz' | 'schoolNameRu' | 'schoolNameKo') => () => {
    setTouched(prev => ({ ...prev, [field]: true }))
  }

  const handleSubmit = () => {
    if (hasError) {
      setTouched({
        schoolNameUz: true,
        schoolNameRu: true,
        schoolNameKo: true,
      })
      return
    }

    const nextForm = {
      schoolNameUz: form.schoolNameUz.trim(),
      schoolNameRu: form.schoolNameRu.trim(),
      schoolNameKo: form.schoolNameKo.trim(),
      login: form.login.trim(),
      password: form.password.trim(),
    }

    const loginChanged = nextForm.login !== initialForm.login
    const passwordChanged = nextForm.password !== initialForm.password

    if (
      nextForm.schoolNameKo === initialForm.schoolNameKo &&
      nextForm.schoolNameRu === initialForm.schoolNameRu &&
      nextForm.schoolNameUz === initialForm.schoolNameUz &&
      !loginChanged &&
      !passwordChanged
    ) {
      return toast.warning('Нет изменений для сохранения.')
    }

    const payload: UpdateSchoolPayload = {
      regionCode,
      schoolNameUz: nextForm.schoolNameUz,
      schoolNameRu: nextForm.schoolNameRu,
      schoolNameKo: nextForm.schoolNameKo,
      ...(loginChanged ? { login: nextForm.login || null } : {}),
      ...(passwordChanged ? { password: nextForm.password || null } : {}),
    }

    updateSchoolMutation.mutate(
      {
        schoolId: school.schoolId,
        payload,
      },
      {
        onSuccess: () => {
          onClose()
        },
      },
    )
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>Редактирование школы</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label='Название школы (UZ)'
              value={form.schoolNameUz}
              onChange={handleChange('schoolNameUz')}
              onBlur={handleBlur('schoolNameUz')}
              error={rawErrors.schoolNameUz && touched.schoolNameUz}
              helperText={rawErrors.schoolNameUz && touched.schoolNameUz ? 'Обязательное поле.' : ' '}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label='Название школы (RU)'
              value={form.schoolNameRu}
              onChange={handleChange('schoolNameRu')}
              onBlur={handleBlur('schoolNameRu')}
              error={rawErrors.schoolNameRu && touched.schoolNameRu}
              helperText={rawErrors.schoolNameRu && touched.schoolNameRu ? 'Обязательное поле.' : ' '}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label='Название школы (KO)'
              value={form.schoolNameKo}
              onChange={handleChange('schoolNameKo')}
              onBlur={handleBlur('schoolNameKo')}
              error={rawErrors.schoolNameKo && touched.schoolNameKo}
              helperText={rawErrors.schoolNameKo && touched.schoolNameKo ? 'Обязательное поле.' : ' '}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label='Логин'
              value={form.login}
              onChange={handleChange('login')}
              disabled={isCredentialsFetching}
              helperText={isCredentialsFetching ? 'Загрузка учетных данных...' : ' '}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label='Пароль'
              type='password'
              value={form.password}
              onChange={handleChange('password')}
              disabled={isCredentialsFetching}
              helperText={isCredentialsFetching ? 'Загрузка учетных данных...' : ' '}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color='inherit'>
          Отмена
        </Button>
        <Button onClick={handleSubmit} variant='contained' disabled={updateSchoolMutation.isLoading || isCredentialsFetching}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  )
}
